import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react'
import { 
  ResizableImage,
  LinkBubbleMenuHandler,
  LinkBubbleMenu,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonEditLink,
  MenuButtonImageUpload,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonUndo,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditor,
  MenuButtonAlignJustify,
  MenuButtonAlignCenter,
  MenuButtonAlignLeft,
  MenuButtonAlignRight,
  TableBubbleMenu,
  MenuButtonAddTable,
  TableImproved,
  MenuButton,
  ControlledBubbleMenu,
} from "mui-tiptap";
import StarterKit from "@tiptap/starter-kit";
import { Link } from "@tiptap/extension-link";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import Youtube from '@tiptap/extension-youtube'
import PropTypes from 'prop-types';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { mergeAttributes } from '@tiptap/core';

import { Box, TextField, Button, Typography, CircularProgress } from '@mui/material';
import { IconBrandYoutubeFilled, IconEye } from '@tabler/icons-react';

import { fileToBase64 } from "@utils/helper";

import { AdminApiEndpoints, AssetUrl } from '@configuration/apiEndpoints';

import { useAuthentication } from '@redux/authentication/hook';

const isValidYouTubeUrl = (url) => {
  const regex = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([\w-]{11})/;
  return regex.test(url);
};

const AlignedResizableImage = ResizableImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: 'left',
        parseHTML: element => element.getAttribute('data-align') || 'left',
        renderHTML: attributes => {
          let justify = 'center';

          if (attributes.align === 'left') justify = 'flex-start';
          if (attributes.align === 'right') justify = 'flex-end';          

          return {
            'data-align': attributes.align,
            style: `align-self: ${justify}`,
          };
        },
      },
      width: {
        default: null,
        parseHTML: element => element.getAttribute('width'),
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return { 
            width: attributes.width,
            style: `width: ${attributes.width}`,
          };
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    // This wraps the <img> inside a <div> with inline styles
    return [
      'div',
      { style: 'width: 100%; display: flex; flex-direction: column;' },
      [
        'img',
        mergeAttributes(HTMLAttributes)
      ]
    ];
  },
});

const CustomRichTextEditor = forwardRef(({ disabled = false, value = "", onChange }, ref) => {
  const localRef = useRef(null); // Internal ref for the RichTextEditor
  const { user } = useAuthentication()

  // Expose the editor instance to the parent
  useImperativeHandle(ref, () => ({
    editor: localRef.current?.editor,
    focus: () => localRef.current?.editor?.commands.focus(), // Add methods you want to expose
  }));

  const uploadImageMutation = useMutation({
    mutationFn: (file) => {
      const formData = new FormData()
      formData.append("source", "RTE")
      formData.append("by", user?.username)
      formData.append("images", file)
      return axios.post(AdminApiEndpoints.uploadImage, formData, {headers: {"Content-Type": "multipart/form-data"}})
    },
    onSuccess: (res) => {
      // console.log(res?.data)
      //Save in recent uploaded
    },
    onError: (err) => {
      // if (err?.response) openSnackbar(err?.response?.data, "error");
      // else openSnackbar("Failed to create banner", "error");
    }
  })

  const [youtubeMenuAnchorEl, setYoutubeMenuAnchorEl] = useState(null);
  const [youtubeMenuVisible, setYoutubeMenuVisible] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [previewWindow, setPreviewWindow] = useState(null);

  const editor = localRef.current?.editor;


  const openPreviewTab = () => {
    const preview = window.open('', 'Live Preview', 'width=800,height=600');
    if (preview) {
      preview.document.write(`
        <html>
          <head>
            <title>Live Preview</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                background-color: #f9f9f9;
                color: #333;
              }

              #content {
                background: white;
                padding: 20px;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                max-width: 800px;
                margin: auto;
                min-height: 100vh;
              }

              img {
                max-width: 100%;
                height: auto;
                width: auto;
              }

              table {
                width: 100%;
                border-collapse: collapse;
              }

              table, th, td {
                border: 1px solid #ccc;
                padding: 8px;
              }
            </style>
          </head>
          <body>
            <div id="content"></div>
            <script>
              window.addEventListener('message', function(e) {
                document.getElementById('content').innerHTML = e.data;
              }, false);
            </script>
          </body>
        </html>
      `);
      preview.document.close();

      setPreviewWindow(preview);

      // ✅ Immediately send current content after a short delay
      setTimeout(() => {
        if (editor && !preview.closed) {
          const html = editor.getHTML();
          preview.postMessage(html, '*');
        }
      }, 500); // small delay to ensure preview is fully ready
    }
  };

  const handleEditorChange = (content) => {
    if (previewWindow && !previewWindow.closed) {
      previewWindow.postMessage(content, '*');
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    };
  }, [previewWindow]);

  return (
    <RichTextEditor
      ref={localRef} // Use the internal ref here
      extensions={[
        StarterKit,
        AlignedResizableImage.configure({
          allowBase64: true,
        }),
        Link.extend({
          inclusive: false,
        }).configure({
          autolink: true,
          linkOnPaste: true,
          openOnClick: false,
        }),
        LinkBubbleMenuHandler,
        TableImproved.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        TextAlign.configure({
          types: ["paragraph", "heading"],
        }),
        Youtube.configure({
          controls: false,
          nocookie: true,
        }),
      ]}
      editable={!disabled}
      content={value}
      onUpdate={({ editor }) => {
        const HTML = editor.getHTML()
        handleEditorChange(HTML)
        onChange(HTML);
      }}
      renderControls={() => (
        <MenuControlsContainer>
          <MenuButtonUndo />
          <MenuButtonRedo />
          <MenuDivider />
          <MenuSelectHeading />
          <MenuDivider />
          <MenuButtonBold />
          <MenuButtonItalic />
          <MenuDivider />
          <MenuButtonAlignLeft 
            onClick={() => {
              const { from, to } = editor.state.selection;
              const selectedNode = editor.state.doc.nodeAt(from);
              if (selectedNode?.type.name === 'text') {
                editor.chain().focus().setTextAlign('left').run();
              }
              if (selectedNode?.type.name === 'image') {
                editor.commands.updateAttributes('image', { align: 'left' });
              }
            }}
          />
          <MenuButtonAlignCenter 
            onClick={() => {
              const { from, to } = editor.state.selection;
              const selectedNode = editor.state.doc.nodeAt(from);
              if (selectedNode?.type.name === 'text') {
                editor.chain().focus().setTextAlign('center').run();
              }
              if (selectedNode?.type.name === 'image') {
                editor.commands.updateAttributes('image', { align: 'center' });
              }
            }}
          />
          <MenuButtonAlignRight 
            onClick={() => {
              const { from, to } = editor.state.selection;
              const selectedNode = editor.state.doc.nodeAt(from);
              if (selectedNode?.type.name === 'text') {
                editor.chain().focus().setTextAlign('right').run();
              }
              if (selectedNode?.type.name === 'image') {
                editor.commands.updateAttributes('image', { align: 'right' });
              }
            }}
          />
          <MenuButtonAlignJustify 
            disabled={editor?.isActive('image') || disabled}
          />
          <MenuDivider />
          <MenuButtonBulletedList />
          <MenuButtonOrderedList />
          <MenuDivider />
          <MenuButtonAddTable />
          <MenuDivider />
          <MenuButtonEditLink />
          <MenuDivider />
          <div style={{position: 'relative'}}>
            {uploadImageMutation.isPending &&
            <div style={{position: 'absolute', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              <CircularProgress color="primary" size={24} disableShrink/>
            </div>
            }
            <MenuButtonImageUpload
              disabled={uploadImageMutation.isPending || disabled}
              onUploadFiles={(files) =>
                Promise.all(
                  files.map(async (file) => {
                    const { data } = await uploadImageMutation.mutateAsync(file)
                    return {
                      src: `${AssetUrl}${data?.imgUrl}`,
                      alt: file.name,
                    };
                    // const base64 = await fileToBase64(file);
                    // return {
                    //   src: base64,
                    //   alt: file.name,
                    // };
                  })
                )
              }
            />
          </div>
          <MenuDivider />
          <MenuButton
            tooltipLabel="YouTube"
            onClick={(event) => {
              setYoutubeMenuAnchorEl(event.currentTarget);
              setYoutubeMenuVisible(true);
            }}
            disabled={disabled}
          >
            <IconBrandYoutubeFilled size={20} />
          </MenuButton>
          <MenuDivider />
          <MenuButton
            tooltipLabel="Preview"
            onClick={openPreviewTab}
          >
            <IconEye size={20} />
          </MenuButton>
        </MenuControlsContainer>
      )}
    >
      {() => (
        <>
          <LinkBubbleMenu />
          <TableBubbleMenu />
          {editor && (
            <ControlledBubbleMenu
              editor={editor}
              open={youtubeMenuVisible}
              anchorEl={youtubeMenuAnchorEl}
              placement="bottom"
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  p: "12px 16px",
                  backgroundColor: "#fff",
                  borderRadius: "5px",
                  gap: "12px",
                  width: "300px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <Typography variant="h6">Add YouTube Video</Typography>
                  <TextField
                    label="Link"
                    variant="outlined"
                    size="small"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    required
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setYoutubeMenuVisible(false);
                      setYoutubeMenuAnchorEl(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => {
                      if (isValidYouTubeUrl(youtubeUrl)) {
                        const videoId = youtubeUrl.split("v=")[1]?.split("&")[0];
                        const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                        editor.commands.setYoutubeVideo({
                          src: embedUrl,
                          width: 640,
                          height: 480,
                        });
                        setYoutubeMenuVisible(false);
                        setYoutubeMenuAnchorEl(null);
                      } else {
                        alert("Invalid YouTube URL");
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
              </Box>
            </ControlledBubbleMenu>
          )}
        </>
      )}
    </RichTextEditor>
  );
});

CustomRichTextEditor.propTypes = {
  editable: PropTypes.bool,
  content: PropTypes.string,
};

export default CustomRichTextEditor;
